/**
 * LLM Settings API Service
 * Provides methods for managing LLM provider configurations
 */

import {
  LLMProviderStatus,
  SavedProvider,
  LLMProviderConfig,
  LLMProviderDetail,
  LLMProviderRequest,
  SaveProviderResponse,
  ProviderActionResponse
} from '../types/llmSettings'
import { config } from '../config'

// Define types for test provider functionality
export interface LLMTestRequest {
  providerId?: number;
  config?: LLMProviderConfig; // Provider configuration object for testing new configurations
  testPrompt: string;
}

export interface LLMTestResponse {
  success: boolean;
  response_time_ms: number;
  tested_at: string;
  response_text?: string;
  error_message?: string;
}

/**
 * LLM Settings API Service Class
 * Handles all interactions with the LLM provider configuration endpoints
 */
export class LLMSettingsApi {
  private baseUrl: string;

  constructor(baseUrl: string = config.backendUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the current status of LLM provider configuration
   */
  async getStatus(): Promise<LLMProviderStatus> {
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers?include_status=true`);
    
    if (!response.ok) {
      throw new Error(`Failed to get provider status: ${response.statusText}`);
    }
    
    const data = await response.json();
    // Extract nested status from consolidated endpoint response
    const statusData = data.status || data;
    // Convert snake_case response to camelCase
    return this.toCamelCase(statusData) as LLMProviderStatus;
  }

  /**
   * Get list of all saved LLM providers
   */
  async listProviders(): Promise<SavedProvider[]> {
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers`);
    
    if (!response.ok) {
      throw new Error(`Failed to list providers: ${response.statusText}`);
    }
    
    const data = await response.json();
    // Convert snake_case response to camelCase
    return this.toCamelCase(data) as SavedProvider[];
  }

  /**
   * Convert camelCase to snake_case for API compatibility
   */
  private toSnakeCase(obj: unknown): unknown {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.toSnakeCase(item));
    if (typeof obj !== 'object') return obj;
    
    const snakeObj: Record<string, unknown> = {};
    for (const key in obj) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      snakeObj[snakeKey] = this.toSnakeCase((obj as Record<string, unknown>)[key]);
    }
    return snakeObj;
  }

  /**
   * Convert snake_case to camelCase for frontend compatibility
   */
  private toCamelCase(obj: unknown): unknown {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.toCamelCase(item));
    if (typeof obj !== 'object') return obj;
    
    const camelObj: Record<string, unknown> = {};
    for (const key in obj) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      camelObj[camelKey] = this.toCamelCase((obj as Record<string, unknown>)[key]);
    }
    return camelObj;
  }

  /**
   * Save a new LLM provider configuration
   */
  async saveProvider(request: LLMProviderRequest): Promise<SaveProviderResponse> {
    // Convert camelCase to snake_case for backend compatibility
    const snakeCaseRequest = {
      config: this.toSnakeCase(request.config),
      make_active: request.makeActive
    };
    
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(snakeCaseRequest),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save provider: ${errorText || response.statusText}`);
    }
    
    return response.json();
  }


  /**
   * Activate a specific LLM provider
   */
  async activateProvider(providerId: number): Promise<ProviderActionResponse> {
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers/${providerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ is_active: true }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to activate provider: ${errorText || response.statusText}`);
    }
    
    return response.json();
  }

  /**
   * Delete an LLM provider configuration
   */
  async deleteProvider(providerId: number): Promise<ProviderActionResponse> {
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers/${providerId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // Parse error message for better handling
      try {
        const errorObj = JSON.parse(errorText);
        throw new Error(errorObj.detail || errorText);
      } catch {
        throw new Error(errorText || response.statusText);
      }
    }
    
    return response.json();
  }


  /**
   * Get a specific LLM provider configuration
   */
  async getProvider(providerId: number): Promise<LLMProviderDetail> {
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers/${providerId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get provider configuration: ${response.statusText}`);
    }
    
    const data = await response.json();
    // Convert snake_case response to camelCase
    return this.toCamelCase(data) as LLMProviderDetail;
  }

  /**
   * Update an existing LLM provider configuration
   */
  async updateProvider(
    providerId: number, 
    config: Partial<LLMProviderConfig>,
    makeActive: boolean = false
  ): Promise<SaveProviderResponse> {
    // Wrap config in request structure like saveProvider does
    const snakeCaseRequest = {
      config: this.toSnakeCase(config),
      make_active: makeActive
    };
    
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers/${providerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(snakeCaseRequest),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update provider: ${errorText || response.statusText}`);
    }
    
    return response.json();
  }

  /**
   * Test LLM provider connectivity
   */
  async testProvider(request: LLMTestRequest): Promise<LLMTestResponse> {
    const snakeCaseRequest: Record<string, unknown> = {
      test_prompt: request.testPrompt
    };

    // Add providerId only if it's defined (for editing existing providers)
    if (request.providerId !== undefined) {
      snakeCaseRequest.provider_id = request.providerId;
    }

    // Add config if provided (for testing new configurations)
    if (request.config) {
      snakeCaseRequest.config = this.toSnakeCase(request.config);
    }
    
    const response = await fetch(`${this.baseUrl}/api/settings/llm-providers/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(snakeCaseRequest),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to test provider: ${errorText || response.statusText}`);
    }

    const result = await response.json();

    // Check if the test was successful
    if (result.success === false) {
      throw new Error(result.error_message || 'Provider test failed');
    }

    return result;
  }

}

// Export a default instance for convenience
export const llmSettingsApi = new LLMSettingsApi();

// Also export the class for testing and custom instances
export default LLMSettingsApi;